// Módulo Agenda — Dashboard + Agendamentos da equipe. Backend real
// (2026-09-13, `/opt/gpsdrive/gpsdrive-backend-release-fase1-2/src/agenda/`)
// substituindo o mock em memória que existia antes (pedido: "apaga os
// dados fake, vamos deixar os dados reais"). Mesmo padrão authGuard +
// request.user.companyId de todo o resto do GPS Drive Backend.
//
// Cliente (pedido 2026-09-14) agora é selecionado a partir dos clientes
// reais da empresa (mesma lista da tela Usuários — useUsers() + accountType
// "usuario", ver agenda-appointment-form-dialog.tsx) — clientUserId é o
// traccarUserId, clientName continua SNAPSHOT do nome (não existe módulo
// de Clientes PRÓPRIO da Agenda ainda, nav item "Clientes" continua "Em
// breve", ver agenda-shell.tsx). Veículo é outra entidade externa real:
// reaproveita useCompanyFleet() (GET /api/v1/vehicles, já isolado por
// empresa) — nunca duplica cadastro de veículo. Técnico e Tópico são
// catálogos reais próprios da Agenda (AgendaTechnician/AgendaTopic —
// AgendaServiceType existiu até 2026-09-15, removido do sistema a
// pedido: "quero remover só o serviços não ter isso no sistema", o campo
// "Tópico" do agendamento passou a usar AgendaTopic no lugar).
import { gpsDriveDelete, gpsDriveGet, gpsDrivePost, gpsDrivePut } from "@/lib/api/gpsdrive-client";

export type AppointmentStatus = "agendado" | "em_andamento" | "finalizado" | "cancelado";

// "Local do atendimento" (pedido 2026-09-14) — "loja" não usa nenhum dos
// campos de endereço, "externa" exige cep/rua/cidade/estado (número e
// complemento sempre digitados, o CEP só resolve o resto via ViaCEP, ver
// agenda-appointment-form-dialog.tsx).
export type AgendaLocationType = "loja" | "externa";

// Atendimento externo é marcado por turno, não por horário (pedido
// 2026-10-13). Não há coluna própria: o turno vira o intervalo
// start/end abaixo e é lido de volta pela hora de início.
export type AgendaShift = "manha" | "tarde";
export const AGENDA_SHIFTS: Record<AgendaShift, { label: string; startHour: number; endHour: number }> = {
  manha: { label: "Manhã", startHour: 8, endHour: 12 },
  tarde: { label: "Tarde", startHour: 13, endHour: 18 },
};
export function agendaShiftOf(start: string | Date): AgendaShift {
  return new Date(start).getHours() < 12 ? "manha" : "tarde";
}

// Login PRÓPRIO do técnico (pedido 2026-09-15: "criar usuario e senha do
// tecnico... ele não pode acessar pelo mesmo login do rastreamento") —
// `username` é devolvido pra exibição, senha NUNCA volta do backend.
export interface AgendaTechnician {
  id: string;
  name: string;
  phone: string | null;
  username: string | null;
  active: boolean;
  /** Vê no app todos os atendimentos da empresa, só pra olhar (2026-10-13). */
  viewAllAppointments?: boolean;
}

export interface CreateAgendaTechnicianInput {
  name: string;
  phone?: string | null;
  username?: string | null;
  password?: string | null;
  viewAllAppointments?: boolean;
}

export interface UpdateAgendaTechnicianInput {
  name?: string;
  phone?: string | null;
  username?: string | null;
  password?: string | null;
  active?: boolean;
  viewAllAppointments?: boolean;
}

// Agenda > Parâmetro (2026-09-15) — três catálogos simples (nome + ativo).
// AgendaTopic é usado pelo campo "Tópico" do agendamento (ver
// AgendaAppointment.topicId abaixo); Tag/Causa de encerramento ainda sem
// nenhum vínculo, só os cadastros em si.
// 2026-10-15: "retirada" = cancelamento (técnico não testa: identifica o
// rastreador, marca se foi devolvido e fotografa). checklist null = usa o
// checklist geral (Parâmetro > Checklist).
export type AgendaTopicKind = "instalacao" | "retirada";

export interface AgendaTopic {
  id: string;
  name: string;
  active: boolean;
  kind?: AgendaTopicKind;
  checklist?: AgendaChecklistTemplateItem[] | null;
}

export interface CreateAgendaTopicInput {
  name: string;
  kind?: AgendaTopicKind;
  checklist?: AgendaChecklistTemplateItem[] | null;
}

export interface UpdateAgendaTopicInput {
  name?: string;
  active?: boolean;
  kind?: AgendaTopicKind;
  checklist?: AgendaChecklistTemplateItem[] | null;
}

export interface AgendaTag {
  id: string;
  name: string;
  active: boolean;
}

export interface CreateAgendaTagInput {
  name: string;
}

export interface UpdateAgendaTagInput {
  name?: string;
  active?: boolean;
}

export interface AgendaClosureReason {
  id: string;
  name: string;
  active: boolean;
}

export interface CreateAgendaClosureReasonInput {
  name: string;
}

export interface UpdateAgendaClosureReasonInput {
  name?: string;
  active?: boolean;
}

// Veículo digitado à mão no agendamento (2026-10-14) — cliente sem veículo
// cadastrado. Cadastrar/Editar dispositivo puxa daqui ("Puxar dados da Agenda").
export interface AgendaVehicleData {
  plate: string;
  brand: string;
  model: string;
  year: string;
  color: string;
}

export interface AgendaManualVehicle {
  appointmentId: string;
  start: string;
  clientUserId: number | null;
  clientName: string;
  topicName: string;
  status: string;
  vehicle: AgendaVehicleData;
}

export interface AgendaAppointment {
  id: string;
  title: string;
  clientUserId?: number | null;
  clientName: string;
  /** WhatsApp das Mensagens de Agendamento */
  clientPhone?: string | null;
  /** Última resposta do cliente às mensagens (só na lista) */
  clientReply?: { tag: string | null; text: string; receivedAt: string; unseen?: number } | null;
  vehicleId?: number | null;
  vehicleName?: string | null;
  vehicleData?: AgendaVehicleData | null;
  /** Rastreador que o técnico testa (regra "atribuído"); nulo = vehicleId */
  testDeviceId?: number | null;
  testDeviceName?: string | null;
  technicianId?: string | null;
  technicianName: string;
  topicId?: string | null;
  topicName: string;
  createdByUserId?: string | null;
  createdByName?: string | null;
  location: string;
  locationType: AgendaLocationType;
  postalCode?: string | null;
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  notes: string;
  status: AppointmentStatus;
  /** ISO 8601 (UTC) */
  start: string;
  /** ISO 8601 (UTC) */
  end: string;
  /** Motivo da última reabertura (ver Agenda > Atendimentos) — null se nunca reaberto */
  reopenReason?: string | null;
  /** ISO 8601 (UTC) da última reabertura — null se nunca reaberto */
  reopenedAt?: string | null;
  /** Registro do app do técnico (/tecnico) — horários de cada etapa */
  enRouteAt?: string | null;
  arrivedAt?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  techNotes?: string;
  /** Onde o rastreador ficou no veículo (técnico, 2026-10-15). */
  installLocation?: string;
  /** Causa escolhida pelo técnico ao encerrar (Parâmetro > Causas). */
  closureReasonName?: string | null;
  /** Retirada: rastreador devolvido? null = não informado. */
  deviceReturned?: boolean | null;
  returnedPhotoId?: string | null;
  checklist?: { label: string; done: boolean }[];
  /** Tag fixa obrigatória (2026-10-13). Nulo = agendamento antigo (com bloqueio). */
  blockMode?: AgendaBlockMode | null;
  tags?: { id: string; name: string }[];
  testedDevice?: { id: number; name: string | null; imei: string | null; plate: string | null } | null;
  deviceChecks?: Partial<Record<AgendaDeviceCheckKey, AgendaDeviceCheck>>;
}

// Cliente do "Novo agendamento" (2026-10-09): cliente da empresa ou
// pré-cadastro de Vendas ainda sem usuário (GET /agenda/client-options).
export interface AgendaClientOption {
  kind: "user" | "lead";
  id: string;
  traccarUserId: number | null;
  name: string;
  document: string | null;
  phone: string | null;
  address: {
    postalCode: string | null;
    street: string | null;
    number: string | null;
    complement: string | null;
    neighborhood: string | null;
    city: string | null;
    state: string | null;
  } | null;
  vehicles: { label: string }[];
}

export interface AgendaAppointmentPhoto {
  id: string;
  // "video" desde 2026-10-13 (thumbUrl null).
  kind: "photo" | "video";
  url: string;
  thumbUrl: string | null;
  createdAt: string;
}

// Teste do rastreador no atendimento (2026-10-13).
export type AgendaDeviceCheckKey = "communication" | "position" | "block" | "unblock" | "external";
export interface AgendaDeviceCheck {
  status: "pending" | "ok" | "failed";
  at: string;
  detail: string;
}

export interface AgendaAppointmentLog {
  id: string;
  action: string;
  ok: boolean | null;
  detail: string;
  technicianName: string;
  lat: number | null;
  lng: number | null;
  createdAt: string;
}

export interface CreateAgendaAppointmentInput {
  title: string;
  clientUserId?: number | null;
  clientName: string;
  clientPhone?: string | null;
  vehicleId?: number | null;
  vehicleName?: string | null;
  vehicleData?: AgendaVehicleData | null;
  testDeviceId?: number | null;
  testDeviceName?: string | null;
  technicianId?: string | null;
  technicianName: string;
  topicId?: string | null;
  topicName: string;
  location: string;
  locationType: AgendaLocationType;
  postalCode?: string | null;
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  notes: string;
  status: AppointmentStatus;
  start: string;
  end: string;
  blockMode: AgendaBlockMode;
  tagIds?: string[];
}

export type UpdateAgendaAppointmentInput = Partial<CreateAgendaAppointmentInput>;

// Reabertura de um atendimento finalizado (tela Agenda > Atendimentos) —
// técnico e data são opcionais (mantém os atuais quando omitidos), só o
// motivo é obrigatório. Ver reopenAgendaAppointmentDto no backend.
export interface ReopenAgendaAppointmentInput {
  technicianId?: string | null;
  /** ISO 8601 — omitido mantém a data original */
  start?: string;
  reopenReason: string;
}

// Teste de equipamento pelo técnico (2026-10-08) — regra escolhida pelo
// administrador em Agenda > Técnicos.
export interface AgendaSettings {
  technicianDeviceMode: "proximity" | "assigned";
  proximityRadiusMeters: number;
  allowCommandTest: boolean;
  /** Parâmetro > Checklist (2026-10-13) */
  checklistTemplate?: AgendaChecklistTemplateItem[];
  /** false = ainda a senha padrão do "teste por fora do sistema" (2026-10-15) */
  externalTestPasswordCustom?: boolean;
}

// Tag fixa do agendamento (2026-10-13): sem bloqueio = técnico não testa
// bloqueio/desbloqueio e o checklist esconde os itens "só com bloqueio".
export type AgendaBlockMode = "com_bloqueio" | "sem_bloqueio";
export const AGENDA_BLOCK_MODE_LABEL: Record<AgendaBlockMode, string> = {
  com_bloqueio: "Com bloqueio",
  sem_bloqueio: "Sem bloqueio",
};

export interface AgendaChecklistTemplateItem {
  id?: string;
  label: string;
  /** Só aparece pro técnico em agendamento "Com bloqueio". */
  blockOnly: boolean;
}

export interface AgendaDashboardSummary {
  totalHoje: number;
  totalMes: number;
  agendados: number;
  emAndamento: number;
  finalizados: number;
  atendimentosSemana: { label: string; count: number }[];
  distribuicaoUltimos30Dias: { label: string; value: number }[];
  performanceTecnicos: { name: string; finalizados: number; emAndamento: number }[];
  atendimentosRecentes: {
    id: string;
    title: string;
    clientName: string;
    time: string;
    status: AppointmentStatus;
  }[];
  tecnicosHoje: { name: string; role: string; appointmentsToday: number }[];
}

// Segunda-feira da semana de `date` — mesma convenção usada no resto do
// módulo (semana começa na segunda, não no domingo).
export function startOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0 = domingo
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function buildRangeQuery(from: Date, to: Date): string {
  return `?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`;
}

// Agrega o resumo do Dashboard a partir de agendamentos reais (mesma
// lógica que antes rodava sobre a semana mockada, só que agora sobre dados
// de verdade) — evita um endpoint de agregação próprio no backend: busca
// os últimos 30 dias + o mês corrente inteiro (a janela mais larga das
// duas) numa chamada só e deriva tudo em memória.
async function buildDashboardSummary(): Promise<AgendaDashboardSummary> {
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
  const last30Start = new Date(today);
  last30Start.setDate(last30Start.getDate() - 29);
  last30Start.setHours(0, 0, 0, 0);

  const rangeFrom = last30Start < monthStart ? last30Start : monthStart;
  const rangeTo = monthEnd;

  const [appointments, technicians] = await Promise.all([
    gpsDriveAgendaService.listAppointments(rangeFrom.toISOString(), rangeTo.toISOString()),
    gpsDriveAgendaService.listTechnicians(),
  ]);

  const todayStr = today.toDateString();
  const todaysAppointments = appointments.filter((a) => new Date(a.start).toDateString() === todayStr);
  const monthAppointments = appointments.filter((a) => {
    const d = new Date(a.start);
    return d >= monthStart && d <= monthEnd;
  });

  const weekStart = startOfWeek(today);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);
  const weekAppointments = appointments.filter((a) => {
    const d = new Date(a.start);
    return d >= weekStart && d <= weekEnd;
  });

  const weekdayLabels = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
  const atendimentosSemana = weekdayLabels.map((label, idx) => ({
    label,
    count: weekAppointments.filter((a) => {
      const d = new Date(a.start).getDay();
      const normalized = d === 0 ? 6 : d - 1;
      return normalized === idx;
    }).length,
  }));

  const distribuicaoUltimos30Dias = Array.from({ length: 30 }, (_, i) => {
    const d = new Date(today);
    d.setDate(d.getDate() - (29 - i));
    const dStr = d.toDateString();
    return {
      label: `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`,
      value: appointments.filter((a) => new Date(a.start).toDateString() === dStr).length,
    };
  });

  const activeTechnicians = technicians.filter((t) => t.active);
  const performanceTecnicos = activeTechnicians.map((t) => ({
    name: t.name,
    finalizados: monthAppointments.filter((a) => a.technicianId === t.id && a.status === "finalizado").length,
    emAndamento: monthAppointments.filter((a) => a.technicianId === t.id && a.status === "em_andamento").length,
  }));

  const atendimentosRecentes = [...appointments]
    .sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime())
    .slice(0, 5)
    .map((a) => ({
      id: a.id,
      title: a.title,
      clientName: a.clientName,
      time: new Date(a.start).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }),
      status: a.status,
    }));

  const tecnicosHoje = activeTechnicians.map((t) => ({
    name: t.name,
    role: "Técnico de campo",
    appointmentsToday: todaysAppointments.filter((a) => a.technicianId === t.id).length,
  }));

  return {
    totalHoje: todaysAppointments.length,
    totalMes: monthAppointments.length,
    agendados: monthAppointments.filter((a) => a.status === "agendado").length,
    emAndamento: monthAppointments.filter((a) => a.status === "em_andamento").length,
    finalizados: monthAppointments.filter((a) => a.status === "finalizado").length,
    atendimentosSemana,
    distribuicaoUltimos30Dias,
    performanceTecnicos,
    atendimentosRecentes,
    tecnicosHoje,
  };
}

// Equipamentos levados pelo técnico em atendimento externo (2026-10-23).
// Só o escritório registra; IMEI precisa estar disponível no Estoque.
export type AgendaEquipmentStatus = "na_rua" | "instalado" | "devolvido" | "defeito";
export type AgendaEquipmentRole = "instalar" | "reserva";
export interface AgendaEquipment {
  id: string;
  appointmentId: string;
  imei: string;
  modelo: string;
  chipNumero: string | null;
  technicianName: string;
  status: AgendaEquipmentStatus;
  // Pedido 2026-10-27: um pra instalar + um de reserva por externo.
  role: AgendaEquipmentRole;
  sentByName: string | null;
  resolvedByName: string | null;
  resolvedAt: string | null;
  createdAt: string;
}
export interface AgendaEquipmentOut extends AgendaEquipment {
  appointment: { id: string; title: string; clientName: string; start: string; status: string; city: string | null };
}
export const AGENDA_EQUIPMENT_STATUS_LABEL: Record<AgendaEquipmentStatus, string> = {
  na_rua: "Na rua com o técnico",
  instalado: "Instalado no cliente",
  devolvido: "Voltou ao estoque",
  defeito: "Voltou com defeito",
};

export const gpsDriveAgendaService = {
  listEquipment: (appointmentId: string): Promise<AgendaEquipment[]> =>
    gpsDriveGet(`/api/v1/agenda/appointments/${appointmentId}/equipment`),
  addEquipment: (appointmentId: string, imeis: string[], reserva: string[] = []): Promise<AgendaEquipment[]> =>
    gpsDrivePost(`/api/v1/agenda/appointments/${appointmentId}/equipment`, { imeis, reserva }),
  setEquipmentRole: (appointmentId: string, equipmentId: string, role: AgendaEquipmentRole): Promise<AgendaEquipment[]> =>
    gpsDrivePut(`/api/v1/agenda/appointments/${appointmentId}/equipment/${equipmentId}/role`, { role }),
  resolveEquipment: (
    appointmentId: string,
    equipmentId: string,
    result: Exclude<AgendaEquipmentStatus, "na_rua">,
  ): Promise<AgendaEquipment[]> =>
    gpsDrivePost(`/api/v1/agenda/appointments/${appointmentId}/equipment/${equipmentId}/resolve`, { result }),
  removeEquipment: (appointmentId: string, equipmentId: string): Promise<AgendaEquipment[]> =>
    gpsDriveDelete(`/api/v1/agenda/appointments/${appointmentId}/equipment/${equipmentId}`),
  listEquipmentOut: (): Promise<AgendaEquipmentOut[]> => gpsDriveGet("/api/v1/agenda/equipment/out"),
  getDashboardSummary: (): Promise<AgendaDashboardSummary> => buildDashboardSummary(),

  // Distribuição por tópico (painel com filtro de período, ver
  // agenda-dashboard.tsx#TopicDistributionPanel) — agrupa por topicName
  // os agendamentos reais dentro de [fromIso, toIso]. Usava o catálogo
  // AgendaServiceType até 2026-09-15 (removido do sistema); agora usa
  // AgendaTopic no lugar.
  getDistribuicaoPorTopico: async (
    fromIso: string,
    toIso: string,
  ): Promise<{ label: string; value: number }[]> => {
    const [appointments, topics] = await Promise.all([
      gpsDriveAgendaService.listAppointments(fromIso, toIso),
      gpsDriveAgendaService.listTopics(),
    ]);
    const labels = topics.length > 0 ? topics.map((t) => t.name) : [];
    return labels.map((label) => ({
      label,
      value: appointments.filter((a) => a.topicName === label).length,
    }));
  },

  listTechnicians: (): Promise<AgendaTechnician[]> => gpsDriveGet("/api/v1/agenda/technicians"),
  searchClientOptions: (q: string): Promise<AgendaClientOption[]> =>
    gpsDriveGet(`/api/v1/agenda/client-options?q=${encodeURIComponent(q)}`),
  searchManualVehicles: (q: string): Promise<AgendaManualVehicle[]> =>
    gpsDriveGet(`/api/v1/agenda/manual-vehicles?q=${encodeURIComponent(q)}`),
  getSettings: (): Promise<AgendaSettings> => gpsDriveGet("/api/v1/agenda/settings"),
  updateSettings: (input: AgendaSettings): Promise<AgendaSettings> => gpsDrivePut("/api/v1/agenda/settings", input),
  updateExternalTestPassword: (password: string): Promise<AgendaSettings> =>
    gpsDrivePut("/api/v1/agenda/settings/external-test-password", { password }),
  updateChecklist: (items: AgendaChecklistTemplateItem[]): Promise<AgendaSettings> =>
    gpsDrivePut("/api/v1/agenda/settings/checklist", { items }),
  listAppointmentPhotos: (id: string): Promise<AgendaAppointmentPhoto[]> =>
    gpsDriveGet(`/api/v1/agenda/appointments/${id}/photos`),
  listAppointmentLogs: (id: string): Promise<AgendaAppointmentLog[]> =>
    gpsDriveGet(`/api/v1/agenda/appointments/${id}/logs`),
  createTechnician: (input: CreateAgendaTechnicianInput): Promise<AgendaTechnician> =>
    gpsDrivePost("/api/v1/agenda/technicians", input),
  updateTechnician: (id: string, input: UpdateAgendaTechnicianInput): Promise<AgendaTechnician> =>
    gpsDrivePut(`/api/v1/agenda/technicians/${id}`, input),

  listTopics: (): Promise<AgendaTopic[]> => gpsDriveGet("/api/v1/agenda/topics"),
  createTopic: (input: CreateAgendaTopicInput): Promise<AgendaTopic> =>
    gpsDrivePost("/api/v1/agenda/topics", input),
  updateTopic: (id: string, input: UpdateAgendaTopicInput): Promise<AgendaTopic> =>
    gpsDrivePut(`/api/v1/agenda/topics/${id}`, input),

  listTags: (): Promise<AgendaTag[]> => gpsDriveGet("/api/v1/agenda/tags"),
  createTag: (input: CreateAgendaTagInput): Promise<AgendaTag> =>
    gpsDrivePost("/api/v1/agenda/tags", input),
  updateTag: (id: string, input: UpdateAgendaTagInput): Promise<AgendaTag> =>
    gpsDrivePut(`/api/v1/agenda/tags/${id}`, input),

  listClosureReasons: (): Promise<AgendaClosureReason[]> => gpsDriveGet("/api/v1/agenda/closure-reasons"),
  createClosureReason: (input: CreateAgendaClosureReasonInput): Promise<AgendaClosureReason> =>
    gpsDrivePost("/api/v1/agenda/closure-reasons", input),
  updateClosureReason: (id: string, input: UpdateAgendaClosureReasonInput): Promise<AgendaClosureReason> =>
    gpsDrivePut(`/api/v1/agenda/closure-reasons/${id}`, input),

  listAppointments: (fromIso: string, toIso: string): Promise<AgendaAppointment[]> =>
    gpsDriveGet(`/api/v1/agenda/appointments${buildRangeQuery(new Date(fromIso), new Date(toIso))}`),

  // weekStartIso: segunda-feira da semana pedida (ver startOfWeek acima) —
  // chave de cache natural pra useAgendaAppointments.
  listAppointmentsForWeek: (weekStartIso: string): Promise<AgendaAppointment[]> => {
    const weekStart = new Date(weekStartIso);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);
    return gpsDriveGet(`/api/v1/agenda/appointments${buildRangeQuery(weekStart, weekEnd)}`);
  },

  // Visão Mês (agenda-calendar.tsx) — mesmo endpoint de range, só troca a
  // janela pro mês inteiro (com folga de 1 semana pra cada lado, cobrindo
  // os dias do mês anterior/seguinte que aparecem na grade de 42 células).
  listAppointmentsForMonth: (monthStartIso: string): Promise<AgendaAppointment[]> => {
    const monthStart = new Date(monthStartIso);
    const gridStart = startOfWeek(monthStart);
    const gridEnd = new Date(gridStart);
    gridEnd.setDate(gridEnd.getDate() + 41);
    gridEnd.setHours(23, 59, 59, 999);
    return gpsDriveGet(`/api/v1/agenda/appointments${buildRangeQuery(gridStart, gridEnd)}`);
  },

  createAppointment: (input: CreateAgendaAppointmentInput): Promise<AgendaAppointment> =>
    gpsDrivePost("/api/v1/agenda/appointments", input),
  updateAppointment: (id: string, input: UpdateAgendaAppointmentInput): Promise<AgendaAppointment> =>
    gpsDrivePut(`/api/v1/agenda/appointments/${id}`, input),
  deleteAppointment: (id: string): Promise<void> => gpsDriveDelete(`/api/v1/agenda/appointments/${id}`),
  reopenAppointment: (id: string, input: ReopenAgendaAppointmentInput): Promise<AgendaAppointment> =>
    gpsDrivePost(`/api/v1/agenda/appointments/${id}/reopen`, input),
  // Liberar encerramento sem teste (2026-10-15) — senha do teste do técnico.
  releaseDeviceTest: (id: string, password: string, note: string): Promise<AgendaAppointment> =>
    gpsDrivePost(`/api/v1/agenda/appointments/${id}/release-device-test`, { password, note }),
};
